import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { Download, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { staffApi, errMsg } from "@/lib/api";

const csv = (name, rows) => {
  const head = ["Name", "Mobile", "Email", "Guests", "Registered"];
  const body = rows.map((r) => [r.name, r.mobile, r.email, r.guests ?? "", r.created_at]);
  const text = [head, ...body].map((l) => l.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type: "text/csv" }));
  a.download = `${name.replace(/[^a-z0-9]+/gi, "-")}.csv`;
  a.click();
};

const EventBlock = ({ e, community }) => (
  <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid={`leader-event-${e.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div><p className="font-semibold text-slate-900">{e.title}</p>
        <p className="text-xs text-slate-500">{community} · {format(parseISO(e.date), "EEE, MMM d, yyyy")}{e.location && ` · ${e.location}`}</p></div>
      <div className="flex items-center gap-2">
        <span className="flex items-center gap-1 rounded-full bg-[#EBF3FA] px-3 py-1 text-sm font-semibold text-[#0A3A6B]" data-testid={`leader-headcount-${e.id}`}><Users className="h-4 w-4" /> {e.headcount} coming</span>
        <Button size="sm" variant="outline" disabled={!e.rsvps.length} onClick={() => csv(e.title, e.rsvps)} data-testid={`leader-csv-${e.id}`}><Download className="mr-1 h-4 w-4" /> CSV</Button>
      </div>
    </div>
    {e.rsvps.length > 0 && (
      <div className="mt-3 divide-y divide-slate-100 text-sm">
        {e.rsvps.map((r) => <p key={r.id} className="flex flex-wrap justify-between gap-2 py-2"><span className="font-medium text-slate-800">{r.name}{r.guests > 0 && <span className="text-slate-500"> +{r.guests}</span>}</span><span className="text-slate-500">{r.mobile}{r.email && ` · ${r.email}`}</span></p>)}
      </div>
    )}
  </div>
);

export const LeaderRegistrations = () => {
  const [data, setData] = useState(null);
  useEffect(() => { staffApi().get("/leader/overview").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!data) return null;
  const name = (id) => data.communities.find((c) => c.id === id)?.name || "";
  return (
    <div className="space-y-6" data-testid="leader-registrations">
      <div><h2 className="text-2xl font-bold tracking-tight text-slate-900">Community Registrations</h2>
        <p className="text-sm text-slate-500">{data.communities.map((c) => c.name).join(", ") || "No community assigned yet"}. Events are set up by the church admins.</p></div>
      {data.events.length === 0 && <p className="rounded-2xl bg-white p-10 text-center text-sm text-slate-400" data-testid="leader-no-events">No community events yet.</p>}
      {data.events.map((e) => <EventBlock key={e.id} e={e} community={name(e.community_id)} />)}
      <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="leader-members">
        <div className="flex items-center justify-between"><h3 className="font-semibold text-slate-900">People who asked to join ({data.members.length})</h3>
          <Button size="sm" variant="outline" disabled={!data.members.length} onClick={() => csv("community-members", data.members)} data-testid="leader-members-csv"><Download className="mr-1 h-4 w-4" /> CSV</Button></div>
        <div className="mt-2 divide-y divide-slate-100 text-sm">
          {data.members.map((m) => <p key={m.id} className="flex flex-wrap justify-between gap-2 py-2"><span className="font-medium text-slate-800">{m.name} <span className="text-xs text-slate-400">{name(m.community_id)}</span></span><span className="text-slate-500">{m.mobile}{m.email && ` · ${m.email}`}</span></p>)}
        </div>
      </div>
    </div>
  );
};
